// @givmo/app-charity — `/apps` (shell contract v3, D3/D6).
//
// ══ TWO ANSWERS AT ONE PATH, AND NO THIRD PAGE ═════════════════════════════════════════════════
//
// The full-page app grid the WIP grew here FOLDED into the Admin Console's Apps page (Jonathan,
// 2026-09-02: "keep the /apps page just for the global admin console"). So:
//
//   * a holder of `write_settings` is REPLACE-REDIRECTED to `/org` — one page, `/org` canonical,
//     and the `home-module-grid` contract id lives there and nowhere else;
//   * everyone else gets the "nothing openable" STATEMENT page — the landing rule's last resort. The
//     nine-dot popup is every person's app list, so the statement points at the menu rather than
//     repeating the tiles.
//
// ⚠ THE GATE IS A CAPABILITY, NEVER A TIER (the build law). An unreported list (`null`) is not a
// holder: the redirect is a positive act on a settled positive answer, exactly as the Admin
// Console tile only draws on one.
//
// ⚠ A FAILED MEMBERSHIPS READ RENDERS ITS ERROR + RETRY HERE, in place of the statement. The root
// sends that person here (there is no organization to compute a landing for), and a statement
// about apps in a menu, over a read that never answered, would be a page that lies about being
// calm.
//
// DATA CONTRACT: `page-apps` is the page's root, `apps-statement` its body; `apps-memberships-error`
// (+ `-retry`) the failed-read state. `page-home` is retired (Home is not a thing — R2).

import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Grip, ShieldAlert } from 'lucide-react';
import { Button, Card, CardBody, EmptyState, Icon } from '@givmo/design-system';
import { CAPABILITY_WRITE_SETTINGS, holdsCapability } from '@givmo/org-console';
import { PageShell } from '../../components/page-shell';
import { useCharityConsole } from '../../console/charity-console-context';
import { ORG_ROUTE } from '../../console/console-routes';
import { useActiveOrg } from '../../org/active-org-context';

/** The pinned statement (the confirm render, 2026-09-03). */
export const APPS_STATEMENT_TITLE = 'Your apps are in the menu';
export const APPS_STATEMENT_BODY =
  'Open the app menu at the top right to switch between your Givmo apps, or to ask for access to more.';

export function AppsPage() {
  const { read } = useCharityConsole();
  const { membershipsState, refetch } = useActiveOrg();
  const navigate = useNavigate();
  const holder = holdsCapability(read.capabilities, CAPABILITY_WRITE_SETTINGS);

  useEffect(() => {
    if (!holder) return;
    void navigate({ to: ORG_ROUTE, replace: true });
  }, [holder, navigate]);

  // Nothing for the one frame before the redirect lands.
  if (holder) return null;

  if (membershipsState === 'error') {
    return (
      <PageShell testId="page-apps" title="Apps">
        <Card>
          <CardBody>
            <EmptyState
              testId="apps-memberships-error"
              icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
              title="Couldn’t load your organizations"
              description="We couldn’t load your organizations. Please try again."
              action={
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={refetch}
                  testId="apps-memberships-error-retry"
                >
                  Try again
                </Button>
              }
            />
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell testId="page-apps" title="Apps">
      <Card>
        <CardBody>
          <EmptyState
            icon={<Icon icon={Grip} size={20} aria-hidden />}
            title={APPS_STATEMENT_TITLE}
            description={APPS_STATEMENT_BODY}
            testId="apps-statement"
          />
        </CardBody>
      </Card>
    </PageShell>
  );
}
